// src/dq/SdhFieldView.tsx
// Per-field review: FIELD (business name) | DATA PROFILE (with a clear "empty" bar) | DQ RULES.
// Matches the Review cockpit's visual language. One round-trip per table (sdh_fieldView).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { sdh_fieldView, type FieldRow, type FieldRule } from "../api/sdhAPI";
import { RuleTagBadges } from "./HelpKit";

const DIM_ORDER = ["Completeness", "Validity", "Conformity", "Consistency", "Integrity", "Uniqueness"];
const DIM_TONE: Record<string, string> = {
  Completeness: "green", Validity: "blue", Conformity: "slate", Consistency: "teal", Integrity: "amber", Uniqueness: "sand",
};
const FAM_LABEL: Record<string, string> = {
  conditional_mandatory: "Conditional", conditional_exclusion: "Exclusion", conditional_inclusion: "Inclusion",
  code_domain: "Value set", comparison: "Comparison", referential: "Reference", mandatory: "Mandatory",
  range: "Range", uniqueness: "Uniqueness", mutual_exclusion: "Exclusion", unit_conversion: "Unit",
  formula: "Formula", completeness: "Data gap", paired_presence: "Paired", multiple_of: "Multiple-of",
};
const num = (n?: number | null) => (n == null ? "—" : Number(n).toLocaleString());
const famOf = (r: FieldRule) => FAM_LABEL[(r as any).family || ""] || (r as any).family || "—";
const lifeC = (l?: string) => (l === "Approved" ? "app" : l === "Rejected" ? "rej" : "draft");
// empty-bar severity: for a governed/mandatory field, empties are a problem; otherwise informational
const emptySeverity = (pct: number, mandatory: boolean) =>
  mandatory && pct > 1 ? "bad" : pct >= 60 ? "warn" : pct >= 5 ? "mid" : "ok";

export default function SdhFieldView(
  { entityId, onOpenRule }: { entityId: string; onOpenRule: (ruleId: string) => void }
) {
  const [tables, setTables] = useState<{ table: string; columns: number }[]>([]);
  const [table, setTable] = useState<string>("");
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [q, setQ] = useState("");
  const [onlyRules, setOnlyRules] = useState(true);
  const [fam, setFam] = useState<string | null>(null);
  const [status, setStatus] = useState<"all" | "Approved" | "Draft">("all");

  useEffect(() => { (async () => {
    try { const r = await sdh_fieldView(entityId); setTables(r.tables || []); if (r.tables?.[0]) setTable(r.tables[0].table); } catch {}
  })(); }, [entityId]);
  const load = useCallback(async (t: string) => {
    try { const r = await sdh_fieldView(entityId, t); setFields(r.fields || []); } catch { setFields([]); }
  }, [entityId]);
  useEffect(() => { if (table) load(table); }, [table, load]);

  const label = (f: FieldRow) => (f as any).label || f.column;
  const rulesOf = (f: FieldRow) => (f.rules || []).filter((r) =>
    (!fam || famOf(r) === fam) && (status === "all" || (r as any).lifecycle === status));

  const famCounts = useMemo(() => {
    const m: Record<string, number> = {};
    fields.forEach((f) => (f.rules || []).forEach((r) => { const k = famOf(r); m[k] = (m[k] || 0) + 1; }));
    return m;
  }, [fields]);

  const shown = useMemo(() => fields.filter((f) => {
    const rs = rulesOf(f);
    if (onlyRules && rs.length === 0) return false;
    if ((fam || status !== "all") && rs.length === 0) return false;
    if (q) { const s = `${label(f)} ${f.column} ${f.table}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  }), [fields, onlyRules, q, fam, status]); // eslint-disable-line

  const totals = useMemo(() => {
    const t = { Approved: 0, Draft: 0 };
    fields.forEach((f) => { t.Approved += f.rule_counts.Approved; t.Draft += f.rule_counts.Draft; });
    return t;
  }, [fields]);

  return (
    <div className="fv">
      <style>{CSS}</style>

      <div className="fv-pills">
        {tables.map((t) => (
          <button key={t.table} className={"fv-pill" + (table === t.table ? " on" : "")} onClick={() => setTable(t.table)}>
            {t.table} <span className="ct">{t.columns}</span>
          </button>
        ))}
        <span className="fv-sp" />
        {(["all", "Approved", "Draft"] as const).map((s) => (
          <button key={s} className={"fv-chip " + lifeC(s) + (status === s ? " on" : "")} onClick={() => setStatus(s)}>
            {s === "all" ? "All" : s} {s === "Approved" ? totals.Approved : s === "Draft" ? totals.Draft : ""}
          </button>
        ))}
        <label className="fv-toggle"><input type="checkbox" checked={onlyRules} onChange={(e) => setOnlyRules(e.target.checked)} /> only fields with rules</label>
        <input className="fv-search" placeholder="Find field…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {/* rule-type filter */}
      <div className="fv-fams">
        <span className="fv-famlab">Rule type</span>
        <button className={"fv-fam" + (fam === null ? " on" : "")} onClick={() => setFam(null)}>All</button>
        {Object.keys(famCounts).sort().map((k) => (
          <button key={k} className={"fv-fam" + (fam === k ? " on" : "")} onClick={() => setFam(fam === k ? null : k)}>{k} <span className="ct">{famCounts[k]}</span></button>
        ))}
      </div>

      <div className="fv-head"><div>Field</div><div>Data profile</div><div>Rules ({fam || status !== "all" ? "filtered" : "all"})</div></div>

      {shown.length === 0 ? (
        <div className="fv-empty">No fields match. Clear a filter or turn off “only fields with rules”.</div>
      ) : shown.map((f) => {
        const rs = rulesOf(f);
        const sev = emptySeverity(f.null_pct, !!f.mandatory);
        const byDim: Record<string, FieldRule[]> = {};
        rs.forEach((r) => { const d = (r as any).dq_dimension || "Conformity"; (byDim[d] = byDim[d] || []).push(r); });
        const dims = [...DIM_ORDER.filter((d) => byDim[d]?.length), ...Object.keys(byDim).filter((d) => !DIM_ORDER.includes(d))];
        return (
          <div className="fv-row" key={`${f.table}.${f.column}`}>
            {/* FIELD */}
            <div className="fv-field">
              <div className="fv-fname">{label(f)}</div>
              <div className="fv-fmeta"><span className="phys">{f.table}.{f.column}</span> · {f.data_type}</div>
              <div className="fv-ftags">
                {f.mandatory && <span className="fv-tag mand">mandatory</span>}
                <span className="fv-tag">{rs.length ? `${rs.length} rule${rs.length > 1 ? "s" : ""}` : "no rules"}</span>
              </div>
            </div>

            {/* DATA PROFILE */}
            <div className="fv-prof">
              <div className="fv-prow"><span>Records</span><b>{num(f.records)}</b></div>
              <div className="fv-prow"><span>Distinct</span><b>{num(f.distinct)}</b></div>
              <div className="fv-prow">
                <span>Empty</span>
                <span className={"fv-emptybar " + sev} title={`${f.null_pct}% of rows have no value`}>
                  <i style={{ width: `${Math.min(100, f.null_pct)}%` }} /><em>{f.null_pct}%</em>
                </span>
              </div>
              {f.top_values?.length > 0 && (
                <div className="fv-tops">
                  <div className="fv-toplab">Top values</div>
                  {f.top_values.map((v, i) => (
                    <div key={i} className="fv-topv"><span className="tv">{v.value || "∅ (blank)"}</span><span className="tc">{num(v.count)}</span></div>
                  ))}
                </div>
              )}
            </div>

            {/* RULES */}
            <div className="fv-rules">
              {rs.length === 0 ? <div className="fv-none">No rules for this field{fam ? " of this type" : ""}.</div> :
                dims.map((d) => (
                  <div key={d} className="fv-dimgrp">
                    <div className={"fv-dim tone-" + (DIM_TONE[d] || "slate")}>{d} <span className="n">{byDim[d].length}</span></div>
                    {byDim[d].map((r) => (
                      <div key={r.rule_id} className="fv-rule" onClick={() => onOpenRule(r.rule_id)}>
                        <div className="fv-rname">{r.rule_name}</div>
                        <div className="fv-rbadges">
                          <span className={"fv-lc " + lifeC((r as any).lifecycle)}>{(r as any).lifecycle}</span>
                          <span className="fv-fchip">{famOf(r)}</span>
                          <RuleTagBadges rule={r} compact skipDim />
                        </div>
                        {((r as any).plain || r.business_logic) &&
                          <div className="fv-rwhy">{(r as any).plain || r.business_logic}</div>}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CSS = `
.fv{--ink:#0E2436;--ink2:#33475b;--muted:#6b7c8d;--line:#e3e8ee;--paper:#f5f7f9;--card:#fff;
 --teal:#0e7c7b;--amber:#b7791f;--red:#c8433b;--green:#1f9d6b;--brand:#6d4aff;
 --disp:'Space Grotesk',system-ui,sans-serif;--mono:'JetBrains Mono',ui-monospace,monospace;
 font-family:'Inter',system-ui,sans-serif;color:var(--ink);font-size:13.5px}
.fv-pills{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px}
.fv-pill{all:unset;padding:6px 13px;border-radius:20px;font-size:13px;background:var(--card);color:var(--ink2);border:1px solid var(--line);cursor:pointer}
.fv-pill.on{background:var(--ink);color:#fff;border-color:var(--ink)} .fv-pill .ct{font-family:var(--mono);font-size:11px;opacity:.7;margin-left:3px}
.fv-sp{flex:1}
.fv-chip{all:unset;cursor:pointer;font-size:12px;padding:5px 11px;border-radius:20px;border:1px solid var(--line);background:var(--card);color:var(--ink2)}
.fv-chip.on.app{background:#e6f4ee;color:#0f6b45;border-color:#bfe3d0} .fv-chip.on.draft{background:#fdf6e7;color:var(--amber);border-color:#eccf8f}
.fv-chip.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.fv-toggle{font-size:12px;color:var(--ink2);display:flex;align-items:center;gap:6px}
.fv-search{border:1px solid var(--line);border-radius:9px;padding:7px 12px;font-size:13px;font-family:inherit;outline:none;min-width:150px}
.fv-fams{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-bottom:12px}
.fv-famlab{font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin-right:2px}
.fv-fam{all:unset;cursor:pointer;font-size:12px;padding:4px 10px;border-radius:7px;background:var(--card);border:1px solid var(--line);color:var(--ink2)}
.fv-fam.on{background:var(--brand);color:#fff;border-color:var(--brand)} .fv-fam .ct{font-family:var(--mono);font-size:10.5px;opacity:.7;margin-left:3px}
.fv-head{display:grid;grid-template-columns:1.1fr 1.15fr 2.5fr;background:var(--ink);color:#fff;font-size:10.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;padding:9px 14px;border-radius:10px 10px 0 0}
.fv-row{display:grid;grid-template-columns:1.1fr 1.15fr 2.5fr;background:var(--card);border:1px solid var(--line);border-top:none}
.fv-row>div{padding:14px}
.fv-field{border-right:1px solid var(--line)}
.fv-fname{font-family:var(--disp);font-weight:600;font-size:15px}
.fv-fmeta{font-size:11.5px;color:var(--muted);margin-top:2px} .fv-fmeta .phys{font-family:var(--mono)}
.fv-ftags{display:flex;gap:6px;margin-top:9px;flex-wrap:wrap}
.fv-tag{font-size:11px;color:var(--ink2);background:var(--paper);border:1px solid var(--line);border-radius:20px;padding:2px 9px}
.fv-tag.mand{color:#0f6b45;background:#e6f4ee;border-color:#bfe3d0}
.fv-prof{border-right:1px solid var(--line)}
.fv-prow{display:flex;justify-content:space-between;align-items:center;padding:3px 0;font-size:12.5px;color:var(--muted)}
.fv-prow b{color:var(--ink);font-family:var(--mono);font-size:12.5px}
.fv-emptybar{position:relative;display:inline-flex;align-items:center;justify-content:flex-end;min-width:120px;height:16px;background:#eef1f4;border-radius:8px;overflow:hidden}
.fv-emptybar i{position:absolute;left:0;top:0;height:100%;border-radius:8px}
.fv-emptybar em{position:relative;font-style:normal;font-family:var(--mono);font-size:10.5px;padding-right:6px;color:var(--ink2)}
.fv-emptybar.ok i{background:#cfe8db} .fv-emptybar.mid i{background:#f4e2b8} .fv-emptybar.warn i{background:#f0c48a} .fv-emptybar.bad i{background:#eeb4ae}
.fv-tops{margin-top:10px}
.fv-toplab{font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);margin-bottom:4px}
.fv-topv{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px dotted var(--line);font-size:12px}
.fv-topv .tv{font-family:var(--mono);color:var(--ink2)} .fv-topv .tc{color:var(--muted);font-family:var(--mono)}
.fv-rules{}
.fv-none{color:var(--muted);font-size:12.5px;padding:6px 0}
.fv-dimgrp{margin-bottom:12px}
.fv-dim{display:inline-flex;align-items:center;gap:6px;font-size:10px;letter-spacing:.07em;text-transform:uppercase;font-weight:700;margin:0 0 6px;padding:2px 9px;border-radius:6px}
.fv-dim .n{font-family:var(--mono);font-size:10px;opacity:.7}
.fv-dim.tone-green{background:#e9f5ee;color:#2f7a4f} .fv-dim.tone-blue{background:#eef2f8;color:#43607e}
.fv-dim.tone-slate{background:#eef1f5;color:#586675} .fv-dim.tone-teal{background:#e2f1ef;color:#0b5f5e}
.fv-dim.tone-amber{background:#faedcf;color:#946011} .fv-dim.tone-sand{background:#f5f0e4;color:#8a6d1c}
.fv-rule{padding:8px 10px;border-radius:8px;cursor:pointer;transition:background .12s}
.fv-rule:hover{background:var(--paper)}
.fv-rname{font-weight:600;font-size:13.5px;color:var(--ink)}
.fv-rbadges{display:flex;gap:6px;flex-wrap:wrap;margin:5px 0 3px}
.fv-rbadges span{font-size:10.5px;padding:2px 8px;border-radius:20px}
.fv-lc.app{background:#e6f4ee;color:#0f6b45} .fv-lc.draft{background:#fdf6e7;color:var(--amber)} .fv-lc.rej{background:#fdeceb;color:var(--red)}
.fv-fchip{background:#eef1f6;color:#42586e} .fv-flag{background:#fdeceb;color:var(--red);font-family:var(--mono)}
.fv-cav{background:#fdf6e7;color:var(--amber)} .fv-cf{background:#f5f7f9;color:var(--ink2);font-family:var(--mono)}
.fv-rwhy{font-size:12px;color:var(--ink2);opacity:.9}
.fv-empty{padding:40px;text-align:center;color:var(--muted);background:var(--card);border:1px solid var(--line);border-top:none}
`;
